import React, { useEffect, useState } from "react";
import styled from "@emotion/styled";
import { NavLink } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import {
  Link,
  Breadcrumbs as MuiBreadcrumbs,
  Card as MuiCard,
  CardContent as MuiCardContent,
  Divider as MuiDivider,
  Paper as MuiPaper,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Typography,
  CircularProgress,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  IconButton,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from "@mui/icons-material";
import { Box, spacing } from "@mui/system";
import {
  fetchCoa,
  createAccount,
  updateAccount,
  deleteAccount,
} from "../../../redux/slices/accounting";
import useAppDispatch from "../../../hooks/useAppDispatch";
import useAppSelector from "../../../hooks/useAppSelector";
import useAuth from "../../../hooks/useAuth";

const Card = styled(MuiCard)(spacing);
const CardContent = styled(MuiCardContent)(spacing);
const Divider = styled(MuiDivider)(spacing);
const Breadcrumbs = styled(MuiBreadcrumbs)(spacing);
const Paper = styled(MuiPaper)(spacing);

function CoaList(props: {
  accounts: any;
  isReady: any;
  onAddAccount: () => void;
  onEditAccount: (account: any) => void;
  onDeleteAccount: (account: any) => void;
}) {
  const { accounts, onAddAccount, onEditAccount, onDeleteAccount } = props;

  const combinedRows = [
    ...(accounts.incomeStatement || []),
    ...(accounts.assets || []),
    ...(accounts.liabilities || []),
  ];

  const tree = buildTree(combinedRows);

  return (
    <Card mb={6}>
      <CardContent
        pb={1}
        style={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography variant="h6" gutterBottom>
          Tilikartta
        </Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={onAddAccount}
        >
          Lisää tili
        </Button>
      </CardContent>

      <Paper>
        <Table>
          <TableBody>
            {!props.isReady ? (
              <TableRow>
                <TableCell align="center">
                  <Box
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    height="50vh"
                  >
                    <CircularProgress />
                  </Box>
                </TableCell>
              </TableRow>
            ) : (
              tree.flatMap((node) =>
                renderCoaNode(node, 0, onEditAccount, onDeleteAccount)
              )
            )}
          </TableBody>
        </Table>
      </Paper>
    </Card>
  );
}

function renderCoaNode(
  node: any,
  level = 0,
  onEditAccount?: (account: any) => void,
  onDeleteAccount?: (account: any) => void
): JSX.Element[] {
  const paddingLeft = level * 20;

  return [
    <TableRow
      key={node.id}
      sx={{
        backgroundColor: level === 0 ? "rgba(0, 0, 0, 0.02)" : "transparent",
        "&:hover": {
          backgroundColor: "rgba(0, 0, 0, 0.01)",
        },
      }}
    >
      <TableCell
        style={{
          paddingLeft,
          fontWeight: 600,
          fontSize: level === 0 ? "0.95rem" : "0.875rem",
          paddingTop: level === 0 ? 16 : 12,
          paddingBottom: level === 0 ? 16 : 12,
        }}
      >
        {node.name}
      </TableCell>
    </TableRow>,
    ...(node.accounts || []).map((acc: any) => (
      <TableRow
        key={acc.code}
        sx={{
          "&:hover": {
            backgroundColor: "rgba(0, 0, 0, 0.02)",
          },
          transition: "background-color 0.2s",
        }}
      >
        <TableCell
          style={{
            paddingLeft: paddingLeft + 20,
            paddingTop: 10,
            paddingBottom: 10,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Box>
              <Box
                component="span"
                sx={{ fontWeight: 500, color: "text.secondary", mr: 1 }}
              >
                {acc.code}
              </Box>
              <Box component="span">{acc.name}</Box>
            </Box>
            {acc.id && (
              <Box>
                <IconButton
                  size="small"
                  onClick={() => onEditAccount?.(acc)}
                  sx={{ mr: 0.5 }}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  color="error"
                  onClick={() => onDeleteAccount?.(acc)}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Box>
            )}
          </Box>
        </TableCell>
      </TableRow>
    )),
    ...(node.children || []).flatMap((child: any) =>
      renderCoaNode(child, level + 1, onEditAccount, onDeleteAccount)
    ),
  ];
}

function buildTree(data: any[]): any[] {
  const map = new Map();
  const roots: any[] = [];

  for (const node of data) {
    map.set(node.id, { ...node, children: [] });
  }

  for (const node of data) {
    if (node.parentId) {
      const parent = map.get(node.parentId);
      if (parent) {
        parent.children.push(map.get(node.id));
      }
    } else {
      roots.push(map.get(node.id));
    }
  }

  return roots.sort((a, b) => a.order - b.order);
}

function Coa() {
  const { currentUser } = useAuth();
  const dispatch = useAppDispatch();
  const { accounts, status: accountingStatus } = useAppSelector(
    (state) => state.accounting
  );
  const { status: propertyStatus } = useAppSelector((state) => state.property);

  const [openDialog, setOpenDialog] = useState(false);
  const [editingAccount, setEditingAccount] = useState<any>(null);
  const [formData, setFormData] = useState({
    code: "",
    name: "",
    rowId: "",
  });

  const associationId =
    currentUser?.activeAssociation?.id || currentUser?.associations?.[0]?.id;

  const isReady =
    accountingStatus === "idle" && propertyStatus === "idle" && accounts;

  useEffect(() => {
    if (associationId) {
      dispatch(fetchCoa(associationId));
    }
  }, [dispatch, associationId]);

  const handleOpenDialog = () => {
    setEditingAccount(null);
    setFormData({ code: "", name: "", rowId: "" });
    setOpenDialog(true);
  };

  const handleEditAccount = (account: any) => {
    setEditingAccount(account);
    setFormData({
      code: account.code.toString(),
      name: account.name,
      rowId: account.rowId,
    });
    setOpenDialog(true);
  };

  const handleDeleteAccount = async (account: any) => {
    if (!associationId) return;

    if (
      window.confirm(
        `Haluatko varmasti poistaa tilin ${account.code} - ${account.name}?`
      )
    ) {
      await dispatch(
        deleteAccount({
          id: account.id,
          associationId,
        })
      );
    }
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingAccount(null);
    setFormData({ code: "", name: "", rowId: "" });
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async () => {
    if (!associationId || !formData.code || !formData.name || !formData.rowId) {
      return;
    }

    if (editingAccount) {
      // Update existing account
      await dispatch(
        updateAccount({
          id: editingAccount.id,
          account: {
            code: parseInt(formData.code),
            name: formData.name,
            rowId: formData.rowId,
          },
          associationId,
        })
      );
    } else {
      // Create new account
      await dispatch(
        createAccount({
          account: {
            code: parseInt(formData.code),
            name: formData.name,
            rowId: formData.rowId,
          },
          associationId,
        })
      );
    }

    handleCloseDialog();
  };

  if (!accounts) return null;

  return (
    <React.Fragment>
      <Helmet title="Tilikartta" />
      <Typography variant="h3" gutterBottom display="inline">
        Tilikartta
      </Typography>

      <Breadcrumbs aria-label="Breadcrumb" mt={2}>
        <Link component={NavLink} to="/">
          Etusivu
        </Link>
        <Link component={NavLink} to="/">
          Talous
        </Link>
        <Typography>Tilikartta</Typography>
      </Breadcrumbs>

      <Divider my={6} />

      <CoaList
        accounts={accounts}
        isReady={isReady}
        onAddAccount={handleOpenDialog}
        onEditAccount={handleEditAccount}
        onDeleteAccount={handleDeleteAccount}
      />

      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {editingAccount ? "Muokkaa tiliä" : "Lisää uusi tili"}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                label="Tilin numero"
                type="number"
                fullWidth
                value={formData.code}
                onChange={(e) => handleInputChange("code", e.target.value)}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Tilin nimi"
                fullWidth
                value={formData.name}
                onChange={(e) => handleInputChange("name", e.target.value)}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Ryhmä (rowId)"
                fullWidth
                value={formData.rowId}
                onChange={(e) => handleInputChange("rowId", e.target.value)}
                required
                helperText="Esim. 1.1, 1.2, 2.1, 3.1.1.1 jne."
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Peruuta</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            color="primary"
            disabled={!formData.code || !formData.name || !formData.rowId}
          >
            Tallenna
          </Button>
        </DialogActions>
      </Dialog>
    </React.Fragment>
  );
}

export default Coa;
